import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, CheckCircle, Clock, AlertCircle, Plus, Calendar, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';

const Tasks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'normal'
  });
  const [savingTask, setSavingTask] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/executive/tasks`);
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tasks',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!taskForm.title || !taskForm.assigned_to || !taskForm.due_date) {
      toast({ title: 'Missing fields', description: 'Title, assignee, and due date are required', variant: 'destructive' });
      return;
    }
    setSavingTask(true);
    try {
      const response = await axios.post(`${backendUrl}/api/executive/tasks`, taskForm);
      toast({ title: 'Task Created', description: 'New task has been added' });
      setTasks(prev => [...prev, response.data]);
      setTaskForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'normal' });
      setShowAddDialog(false);
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed to create task', variant: 'destructive' });
    } finally {
      setSavingTask(false);
    }
  };

  const toggleTaskCompletion = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const newCompleted = !task.completed;
    const newStatus = newCompleted ? 'completed' : 'pending';

    try {
      await axios.put(
        `${backendUrl}/api/executive/tasks/${taskId}`,
        {
          completed: newCompleted,
          status: newStatus
        }
      );

      // Update local state
      setTasks(tasks.map(t =>
        t.id === taskId
          ? { ...t, completed: newCompleted, status: newStatus }
          : t
      ));

      toast({
        title: newCompleted ? 'Task completed' : 'Task reopened',
        description: newCompleted ? 'Task marked as completed' : 'Task marked as pending'
      });
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.response?.data?.detail || 'Failed to update task',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'completed': { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      'in-progress': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'In Progress' },
      'pending': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending' },
      'overdue': { bg: 'bg-red-100', text: 'text-red-800', label: 'Overdue' }
    };
    return badges[status] || badges.pending;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      'urgent': { bg: 'bg-red-100', text: 'text-red-800', label: 'Urgent', icon: AlertCircle },
      'high': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High', icon: AlertCircle },
      'normal': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Normal', icon: Clock },
      'low': { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Low', icon: Clock }
    };
    return badges[priority] || badges.normal;
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    return true;
  });

  const taskStats = {
    total: tasks.length,
    completed: tasks.filter(t => t.completed).length,
    pending: tasks.filter(t => t.status === 'pending').length,
    overdue: tasks.filter(t => t.status === 'overdue').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-[#2c3e7d] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-wide">TASKS</h1>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[#2c3e7d] text-white py-16">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/executive')}
            className="text-white hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Executive Members
          </Button>
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-wide">
              TASKS
            </h1>
            <div className="w-24 h-1 bg-white mx-auto mt-4"></div>
            <p className="mt-4 text-lg text-white/90">
              Executive committee tasks and action items
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-5xl mx-auto">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#2c3e7d]">{taskStats.total}</p>
              <p className="text-sm text-gray-600 mt-1">Total Tasks</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{taskStats.completed}</p>
              <p className="text-sm text-gray-600 mt-1">Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-600">{taskStats.pending}</p>
              <p className="text-sm text-gray-600 mt-1">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-600">{taskStats.overdue}</p>
              <p className="text-sm text-gray-600 mt-1">Overdue</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Add Task */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={() => setShowAddDialog(true)} className="bg-[#2c3e7d] hover:bg-[#1e2a5a] w-full md:w-auto">
            <Plus size={18} className="mr-2" />
            Add Task
          </Button>
        </div>

        {/* Tasks List */}
        <div className="max-w-5xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Action Items ({filteredTasks.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredTasks.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-4 text-gray-600">No tasks found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTasks.map((task) => {
                    const statusBadge = getStatusBadge(task.status);
                    const priorityBadge = getPriorityBadge(task.priority);
                    const PriorityIcon = priorityBadge.icon;

                    return (
                      <div
                        key={task.id}
                        className={`flex items-start gap-4 p-4 rounded-lg border-2 transition-colors ${
                          task.completed
                            ? 'bg-gray-50 border-gray-200'
                            : 'bg-white border-gray-200 hover:border-[#2c3e7d]'
                        }`}
                      >
                        <Checkbox
                          checked={task.completed}
                          onCheckedChange={() => toggleTaskCompletion(task.id)}
                          className="mt-1"
                        />
                        
                        <div className="flex-1">
                          <div className="flex flex-wrap items-start gap-2 mb-2">
                            <h4 className={`font-semibold ${
                              task.completed ? 'line-through text-gray-500' : 'text-gray-900'
                            }`}>
                              {task.title}
                            </h4>
                            <span className={`px-2 py-1 text-xs rounded-full ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${priorityBadge.bg} ${priorityBadge.text}`}>
                              <PriorityIcon size={12} />
                              {priorityBadge.label}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p>Assigned to: {task.assigned_to}</p>
                            <p className="flex items-center gap-1">
                              <Calendar size={14} />
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Task Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Task title..."
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Task description..."
                className="min-h-[80px]"
              />
            </div>
            <div>
              <Label>Assigned To *</Label>
              <Input
                value={taskForm.assigned_to}
                onChange={(e) => setTaskForm(prev => ({ ...prev, assigned_to: e.target.value }))}
                placeholder="Person name..."
              />
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select
                value={taskForm.priority}
                onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} className="bg-[#2c3e7d]" disabled={savingTask}>
              {savingTask ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tasks;